// interiorQuotationModel.js - data model + pure helpers for the Interior Quotation create form
// Nesting: Quotation -> Section (A/B/C) -> Room/Category -> Line Item
// A line item can share its "details" text and/or its "Total" with the item directly
// above it (mergeDetailsUp / mergeTotalUp). Only the first item of a contiguous
// merged run ("owner") actually stores the shared value - see computeClusterStarts.

let idCounter = 0;
export const generateId = (prefix = 'id') => {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
};

export const round2 = (num) => {
  const n = Number(num);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

export const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

// ---- Factories -------------------------------------------------------

export const createLineItem = (overrides = {}) => ({
  id: generateId('item'),
  unitName: '',
  widthFt: '',
  heightFt: '',
  depthFt: '',
  qty: '',
  qtyIsManual: false,
  ratePerUnit: '',
  unitTotal: '',
  mergeDetailsUp: false,
  mergeTotalUp: false,
  details: '',
  image: null, // { name, dataUrl } | null - reference photo for this unit, not shared across merge groups
  ...overrides,
});

export const createSectionCItem = (overrides = {}) => ({
  id: generateId('citem'),
  unitName: '',
  qty: '',
  details: '',
  image: null,
  ...overrides,
});

export const createRoom = (name = 'New Room', items = null) => ({
  id: generateId('room'),
  name,
  collapsed: false,
  items: items && items.length ? items : [createLineItem()],
});

export const createSectionCRoom = (name = 'New Category', items = null) => ({
  id: generateId('croom'),
  name,
  collapsed: false,
  items: items && items.length ? items : [createSectionCItem()],
});

// A section is "priced" (full Room/Width/Height/Depth/Rate/Total fields, rolls into the totals
// block) or "informational" (Section-C-style: Unit Name/Qty/Details/Image only, always
// excluded from every total).
export const createSection = (overrides = {}) => ({
  id: generateId('section'),
  name: 'New Section',
  isPriced: true,
  collapsed: false,
  discountPercent: '0', // only meaningful for priced sections - applied to that section's own total
  rooms: [createRoom('New Room')],
  ...overrides,
});

// ---- Room-list helpers (used to update a section's `rooms` array immutably) -----------

export const updateRoomItemsInList = (rooms, roomId, newItems) =>
  rooms.map((r) => (r.id === roomId ? { ...r, items: newItems } : r));

export const toggleRoomCollapsedInList = (rooms, roomId) =>
  rooms.map((r) => (r.id === roomId ? { ...r, collapsed: !r.collapsed } : r));

export const renameRoomInList = (rooms, roomId, name) =>
  rooms.map((r) => (r.id === roomId ? { ...r, name } : r));

export const addRoomToList = (rooms, room) => [...rooms, room];

export const removeRoomFromList = (rooms, roomId) => rooms.filter((r) => r.id !== roomId);

export const moveRoomInList = (rooms, roomId, direction) => {
  const idx = rooms.findIndex((r) => r.id === roomId);
  const target = idx + direction;
  if (idx < 0 || target < 0 || target >= rooms.length) return rooms;
  const arr = [...rooms];
  [arr[idx], arr[target]] = [arr[target], arr[idx]];
  return arr;
};

// ---- Dimension / rate derivations ------------------------------------

export const applyQtyChangeRecomputeTotal = (item) => {
  const rate = parseFloat(item.ratePerUnit);
  const qty = parseFloat(item.qty);
  if (Number.isFinite(rate) && Number.isFinite(qty)) {
    return { ...item, unitTotal: String(round2(rate * qty)) };
  }
  return item;
};

export const applyDimensionChange = (item, field, value) => {
  const next = { ...item, [field]: value };
  if (!next.qtyIsManual) {
    const w = parseFloat(next.widthFt);
    const h = parseFloat(next.heightFt);
    if (Number.isFinite(w) && Number.isFinite(h)) {
      next.qty = String(round2(w * h));
    }
  }
  return applyQtyChangeRecomputeTotal(next);
};

export const applyManualQtyChange = (item, value) => ({
  ...item,
  qty: value,
  qtyIsManual: true,
});

export const resetQtyToAuto = (item) => {
  const w = parseFloat(item.widthFt);
  const h = parseFloat(item.heightFt);
  const qty = Number.isFinite(w) && Number.isFinite(h) ? String(round2(w * h)) : '';
  return applyQtyChangeRecomputeTotal({ ...item, qty, qtyIsManual: false });
};

export const applyRateChange = (item, value) => {
  const next = { ...item, ratePerUnit: value };
  return applyQtyChangeRecomputeTotal(next);
};

// ---- Cluster (merge) math ---------------------------------------------

// For each index, returns the index of the run's owning (first) item.
export const computeClusterStarts = (items, flagKey) => {
  const starts = [];
  let currentStart = 0;
  items.forEach((item, idx) => {
    if (idx === 0 || !item[flagKey]) currentStart = idx;
    starts.push(currentStart);
  });
  return starts;
};

export const computeClusterSizes = (starts) => {
  const sizes = {};
  starts.forEach((start) => {
    sizes[start] = (sizes[start] || 0) + 1;
  });
  return sizes;
};

const cloneItems = (items) => items.map((it) => ({ ...it }));

export const mergeDetailsUp = (items, index) => {
  if (index <= 0) return items;
  const next = cloneItems(items);
  next[index].mergeDetailsUp = true;
  next[index].details = '';
  return next;
};

export const unmergeDetails = (items, index) => {
  if (!items[index]?.mergeDetailsUp) return items;
  const starts = computeClusterStarts(items, 'mergeDetailsUp');
  const ownerIdx = starts[index];
  const next = cloneItems(items);
  next[index].mergeDetailsUp = false;
  next[index].details = items[ownerIdx].details;
  return next;
};

export const mergeTotalUp = (items, index) => {
  if (index <= 0) return items;
  const next = cloneItems(items);
  next[index].mergeTotalUp = true;
  next[index].unitTotal = '';
  next[index].ratePerUnit = '';
  return next;
};

export const unmergeTotal = (items, index) => {
  if (!items[index]?.mergeTotalUp) return items;
  const starts = computeClusterStarts(items, 'mergeTotalUp');
  const ownerIdx = starts[index];
  const next = cloneItems(items);
  next[index].mergeTotalUp = false;
  next[index].unitTotal = items[ownerIdx].unitTotal;
  return next;
};

// Fully detach an item from both merge groups (used before reordering).
// If the item is a run owner with a follower, the follower is promoted
// to owner (inherits the shared value) so no data is lost.
export const detachItemForMove = (items, index) => {
  let next = cloneItems(items);

  const detStarts = computeClusterStarts(next, 'mergeDetailsUp');
  if (next[index].mergeDetailsUp) {
    const ownerIdx = detStarts[index];
    next[index].details = next[ownerIdx].details;
    next[index].mergeDetailsUp = false;
  } else if (
    index + 1 < next.length &&
    next[index + 1].mergeDetailsUp &&
    detStarts[index + 1] === index
  ) {
    next[index + 1] = { ...next[index + 1], mergeDetailsUp: false, details: next[index].details };
  }

  const totStarts = computeClusterStarts(next, 'mergeTotalUp');
  if (next[index].mergeTotalUp) {
    const ownerIdx = totStarts[index];
    next[index].unitTotal = next[ownerIdx].unitTotal;
    next[index].mergeTotalUp = false;
  } else if (
    index + 1 < next.length &&
    next[index + 1].mergeTotalUp &&
    totStarts[index + 1] === index
  ) {
    next[index + 1] = { ...next[index + 1], mergeTotalUp: false, unitTotal: next[index].unitTotal };
  }

  return next;
};

export const moveItem = (items, index, direction) => {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= items.length) return items;
  let next = detachItemForMove(items, index);
  next = detachItemForMove(next, targetIndex);
  const arr = [...next];
  [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];
  return arr;
};

export const addItemAfter = (items, index) => {
  const next = [...items];
  next.splice(index + 1, 0, createLineItem());
  return next;
};

export const removeItem = (items, index) => {
  const removed = items[index];
  const rest = items.filter((_, i) => i !== index);
  const followerExisted = index < items.length - 1;

  if (followerExisted) {
    if (!removed.mergeDetailsUp && items[index + 1]?.mergeDetailsUp) {
      rest[index] = { ...rest[index], mergeDetailsUp: false, details: removed.details };
    }
    if (!removed.mergeTotalUp && items[index + 1]?.mergeTotalUp) {
      rest[index] = { ...rest[index], mergeTotalUp: false, unitTotal: removed.unitTotal };
    }
  }

  return rest;
};

// ---- Totals -------------------------------------------------------------

export const sumRoomTotal = (items) => {
  const starts = computeClusterStarts(items, 'mergeTotalUp');
  let total = 0;
  items.forEach((item, idx) => {
    if (starts[idx] === idx) total += toNumber(item.unitTotal);
  });
  return total;
};

export const sumSectionTotal = (rooms) =>
  rooms.reduce((sum, room) => sum + sumRoomTotal(room.items), 0);

// ---- Full-quotation totals -----------------------------------------------
// Single source of truth for the totals block, shared by the live builder
// (CreateInteriorQuatation.js) and the one-shot PDF export of a saved record
// (interiorQuotationPdf.js) so the two never drift apart.
export const computeQuotationTotals = ({ sections, designFeePercent, gstPercent, paymentSchedule }) => {
  const pricedSectionTotals = (sections || [])
    .filter((s) => s.isPriced)
    .map((s) => {
      const total = sumSectionTotal(s.rooms);
      const discountPercent = s.discountPercent ?? '0';
      const discountAmount = total * (toNumber(discountPercent) / 100);
      const afterDiscount = total - discountAmount;
      return { id: s.id, name: s.name, total, discountPercent, discountAmount, afterDiscount };
    });

  const totalPriced = pricedSectionTotals.reduce((sum, s) => sum + s.total, 0);
  const totalAfterDiscount = pricedSectionTotals.reduce((sum, s) => sum + s.afterDiscount, 0);
  const designFeeAmount = totalAfterDiscount * (toNumber(designFeePercent) / 100);
  const amountWithDesignFee = totalAfterDiscount + designFeeAmount;
  const gstAmount = amountWithDesignFee * (toNumber(gstPercent) / 100);
  const finalAmount = amountWithDesignFee + gstAmount;

  const paymentRows = (paymentSchedule || []).map((row) => ({
    ...row,
    amount: finalAmount * (toNumber(row.percent) / 100),
  }));

  return {
    pricedSectionTotals,
    totalPriced,
    totalAfterDiscount,
    designFeeAmount,
    amountWithDesignFee,
    gstAmount,
    finalAmount,
    paymentRows,
  };
};
